import React, { useCallback, useContext } from 'react';
import classNames from 'classnames';
import { t } from 'i18next';
import Dropzone from 'components/Dropzone';
import api from 'api';
import DropzoneRequestsProvider from 'providers/DropzoneRequests';
import { DropzoneUpload } from 'models/Dropzone';
import CurrentProfileContext from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/providers/CurrentProfile/CurrentProfile.context';
import { sentryError } from 'utils';
import CheckToggleField from 'components/CheckToggleField';
import { Field, useField } from 'react-final-form';
import InputField from 'components/InputField';
import validations from 'validations';
import { stringIsEqual } from 'router/subrouters/Dash/subrouters/private/subrouters/Profile/utils';

import './CompanyInputs.styles.scss';
import './CompanyInputs.styles.responsive.scss';

type CompanyInputsProps = {
  className?: string;
};

const CompanyInputs: React.FC<CompanyInputsProps> = (props) => {
  const { className, children } = props;

  const { currentProfile, updateFirmLogo } = useContext(CurrentProfileContext);

  const { _id, firmLogo } = currentProfile || {};

  const {
    input: { value: isBusinessBroker },
  } = useField('isBusinessBroker');

  const classes = classNames('company-inputs', className);

  const uploadCompanyLogo: DropzoneUpload = useCallback(
    async (file, fileName, config) => {
      try {
        const fileUploaded = await api.users.uploadFile(
          _id,
          file,
          fileName,
          config,
        );

        updateFirmLogo(fileUploaded);

        return fileUploaded;
      } catch (error) {
        sentryError(error);
      }
    },
    [_id, updateFirmLogo],
  );

  const deleteCompanyLogo = useCallback(async () => {
    try {
      await api.users.deleteFile(_id, 'firmLogo', firmLogo?._id);

      updateFirmLogo(null);

      return null;
    } catch (error) {
      sentryError(error);
    }
  }, [_id, firmLogo?._id, updateFirmLogo]);

  const fetchFiles = useCallback(
    async () => (firmLogo ? [firmLogo] : []),
    [firmLogo],
  );

  return (
    <div className={classes}>
      <div className="heading">
        <Field
          name="isBusinessBroker"
          type="checkbox"
          component={CheckToggleField}
          label={<span>{t('areYouBroker')}</span>}
        />

        <p>{t('companyPageDesc')}</p>
      </div>

      {isBusinessBroker && (
        <>
          <div>
            <div className="company-wrapper">
              <Field
                placeholder={t('companyName')}
                name="companyName"
                component={InputField}
                isEqual={stringIsEqual}
              />

              <Field
                placeholder={t('abn')}
                name="companyABN"
                component={InputField}
                type="number"
                isEqual={stringIsEqual}
                validate={validations.abnField(t('abnInvalid'))}
              />
            </div>

            <DropzoneRequestsProvider
              uploadRequest={uploadCompanyLogo}
              deleteRequest={deleteCompanyLogo}
              fetchFiles={fetchFiles}
            >
              <Dropzone
                className="company-inputs__dropzone"
                fileName="firmLogo"
                accept="image/*"
                maxFiles={1}
                withProvider
              />
            </DropzoneRequestsProvider>
          </div>

          {children}
        </>
      )}
    </div>
  );
};

export default CompanyInputs;
